import { type ProductType } from '@mc/api'

// https://app.growthbook.io/features/prcng-1138_v1
export const CERTS_QUIZ_PLANS_FLAG = 'prcng-1138_v1'

export type ExperimentBucket = 'control' | 'variant_1'

// Twins of the constants of the same name in the apps/next half of this
// experiment, which is what first puts the param on the URL. `quiz` is written
// by the interstitial; the plans page overwrites it with the outcome of its
// cart write, and only `added` means the cart was read back holding the
// certificate line.
export const CERTS_PARAM = 'certs'

// `preselected` is the off-site campaign entry (PRCNG-1193), which the funnel
// never writes and which enters a visitor regardless of their bucket.
// `addedFromCampaign` and `declinedFromCampaign` carry that off-bucket
// provenance past Continue, which would otherwise be flattened into `added` or
// `declined` before the payment step reads it.
export const CERTS_VALUES = {
  quiz: 'q',
  preselected: 'p',
  added: 'a',
  addedFromCampaign: 'ap',
  declined: 'd',
  declinedFromCampaign: 'dp',
} as const

export type CertsValue = (typeof CERTS_VALUES)[keyof typeof CERTS_VALUES]

// Twin of the narrowing in apps/next: an unrecognised value drops the visitor
// out of the funnel rather than counting as intent.
export const toCertsValue = (value: string | null): CertsValue | null =>
  Object.values(CERTS_VALUES).some((known) => known === value)
    ? (value as CertsValue)
    : null

// Campaign provenance, which every spelling carries — `preselected` on arrival
// and `addedFromCampaign` / `declinedFromCampaign` on the way back from the
// payment step. Distinct from "seed the card selected", which is the arrival
// alone.
export const isCampaignCertsValue = (value?: CertsValue | null) =>
  value === CERTS_VALUES.preselected ||
  value === CERTS_VALUES.addedFromCampaign ||
  value === CERTS_VALUES.declinedFromCampaign

// PurchasePlan::CERT_CREDIT_ADD_ON_ANNUAL_PURCHASE_PASS_V1's cart line type.
// Shared with the Learning Toolkit and On Call add-ons, so it is never a
// discriminator — only what we send. Typed so it cannot drift from the enum.
export const CERT_CREDIT_ADD_ON_PRODUCT_TYPE: ProductType =
  'add_on_subscription'

// PurchasePlan::CERT_CREDIT_ADD_ON_ANNUAL_PURCHASE_PASS_V1. Matched by slug
// rather than by `productType`, which is `add_on_subscription` for the Learning
// Toolkit and On Call add-ons too — the backend discriminates the same way
// (PurchasePlan#is_cert_credit_add_on?).
export const CERT_CREDIT_ADD_ON_SLUG =
  'certificate-credit-add-on-annual-purchase-pass-v1'
